import { defaultPermissions, model, v4OnlyPermissions } from '@/config/casbin/accessControl';
import { resolveUiV4 } from '@/lib/uiFlag';
import { useAuthStore } from '@/store';
import { CanParams } from '@refinedev/core';
import { newEnforcer, StringAdapter } from 'casbin';
import { calculateResourcePermissions } from './resourcePermissions';
import { generateCasbinRBACPolicies } from './utils';

// LRU Cache implementation for Casbin enforcers
class LRUCache<K, V> {
  private maxSize: number;
  private cache: Map<K, V>;

  constructor(maxSize = 100) {
    this.maxSize = maxSize;
    this.cache = new Map();
  }

  get(key: K): V | undefined {
    const value = this.cache.get(key);
    if (value !== undefined) {
      // Move to end (most recently used)
      this.cache.delete(key);
      this.cache.set(key, value);
    }
    return value;
  }

  set(key: K, value: V): void {
    // If key already exists, delete it first
    if (this.cache.has(key)) {
      this.cache.delete(key);
    } else if (this.cache.size >= this.maxSize) {
      // Remove least recently used (first entry)
      const firstKey = this.cache.keys().next().value;
      if (firstKey !== undefined) {
        this.cache.delete(firstKey);
      }
    }

    // Add new entry (most recently used)
    this.cache.set(key, value);
  }

  has(key: K): boolean {
    return this.cache.has(key);
  }

  delete(key: K): boolean {
    return this.cache.delete(key);
  }

  clear(): void {
    this.cache.clear();
  }

  size(): number {
    return this.cache.size;
  }

  keys(): IterableIterator<K> {
    return this.cache.keys();
  }
}

// Cache for Casbin enforcers with LRU eviction policy
// Maximum 200 cached enforcers to prevent memory leaks
const enforcerCache = new LRUCache<string, any>(200);

// Track current user role to detect changes
let currentTrackedCacheKey: string | null = null;

// Helper function to generate cache key based on user role, permissions, and org type
function generateCacheKey(orgType: string, userRole: string, userPermissions: any): string {
  return `${orgType}_${userRole}_${JSON.stringify(userPermissions ?? {})}`;
}

// Helper function to get or create enforcer with caching
async function getEnforcer(currentUser: any) {
  const userRole = currentUser?.role ?? '';
  const userPermissions = currentUser?.permission ?? {};
  const orgType = currentUser?.org?.org_type ?? '';
  const cacheKey = generateCacheKey(orgType, userRole, userPermissions);
  checkAndClearCacheOnChanges(cacheKey);

  // Check if enforcer exists in cache
  if (enforcerCache.has(cacheKey)) {
    return enforcerCache.get(cacheKey);
  }

  // Create new enforcer if not in cache
  let policyString = defaultPermissions;

  // v4-shell-only FE policies (see accessControl.ts) — the flag is constant
  // for the JS lifetime (toggling it reloads), so caching stays coherent.
  if (resolveUiV4()) {
    policyString += '\n' + v4OnlyPermissions;
  }

  // Add user-specific policies
  if (currentUser?.role && currentUser?.permission) {
    const userPolicies = generateCasbinRBACPolicies(currentUser.role, currentUser.permission);
    policyString += '\n' + userPolicies;
  }

  const enforcer = await newEnforcer(model, new StringAdapter(policyString));

  // Cache the enforcer
  enforcerCache.set(cacheKey, enforcer);

  return enforcer;
}

// Function to check for role changes and clear cache if needed
function checkAndClearCacheOnChanges(cacheKey: string | null) {
  if (currentTrackedCacheKey !== cacheKey) {
    enforcerCache.clear();
  }
  currentTrackedCacheKey = cacheKey;
}

// // Helper function to check if organization type has any policy for given resource and action
// async function hasPolicy(
//   enforcer: any,
//   policyType: string,
//   resource: string | undefined,
//   action: string
// ): Promise<boolean> {
//   const policies = await enforcer.getFilteredPolicy(0, policyType, resource);

//   return policies.some((policy: string[]) => {
//     const policyAction = policy[2]; // Third element is the action
//     // Check if the action matches (handle patterns like "(list)" or "(list)|(create)")
//     return policyAction.includes(action);
//   });
// }

export const accessControlProvider = {
  can: async ({ resource, action, params }: CanParams) => {
    try {
      const currentUser: any = useAuthStore.getState().user;
      // const orgType = currentUser?.org?.org_type;
      // const orgType = 'district';
      const currentUserRole = currentUser?.role ?? null;
      // const orgTypeWithRole = `${orgType}_${currentUserRole}`;

      // Get cached or create enforcer
      const enforcer = await getEnforcer(currentUser);

      // Check if user role has any policy for this resource and action
      // const hasUserRoleRBACPolicy = await hasPolicy(enforcer, currentUserRole, resource, action);

      // Check if org type has any policy for this resource and action
      // const hasOrgRBACPolicy = await hasPolicy(enforcer, orgType, resource, action);

      // For districts, check district-specific role policies (e.g., district_admin)=
      // const hasOrgRoleRBACPolicy = await hasPolicy(enforcer, orgTypeWithRole, resource, action);

      // Check permissions for different authorization levels
      const hasUserRolePermission = await enforcer.enforce(currentUserRole, resource, action);

      // const hasOrgPermission = await enforcer.enforce(orgType, resource, action);

      // const hasOrgRolePermission = await enforcer.enforce(orgTypeWithRole, resource, action);

      const { hasResourcePermission, reason } = calculateResourcePermissions({
        resource,
        action,
        params,
        currentUser,
      });

      //////////////////////////////////////////////////////////
      // NOTE: Policy means Resource and Action combination
      // NOTE: Following is the order of checking permissions
      // 1. Current User Permission defined as RBAC Policy at FE - accessControl.ts
      // 2. Current User Permissions coming from BE changed to RBAC Policy at FE - generateCasbinRBACPolicies.ts
      // 3. If Org Type Policy exists, then check if Org Type Permission allowed - accessControl.ts
      // 4. If Org Type with Role Policy exists, then check if Org Type with Role Permission allowed - accessControl.ts
      // 5. By default Resource Permission is true and with conditions we can disable it - resourcePermissions.ts

      // NOTE: If any of the above permissions are false, then the permission is false

      // NOTE: When No BE RBAC policy exist for resource or action then we are adding RBAC policy at FE in accessControl.ts and if required we can disable them by adding conditions in resourcePermissions.ts
      // eg. users (update_accommodations),(send_welcome_email_to_user), etc.
      //////////////////////////////////////////////////////////

      // Simplified permission logic
      const hasPermission = hasUserRolePermission && hasResourcePermission;
      // if (hasOrgRBACPolicy) hasPermission = hasPermission && hasOrgPermission;
      // if (hasOrgRoleRBACPolicy) hasPermission = hasPermission && hasOrgRolePermission;

      // const hasAnyRBACPolicy = hasUserRoleRBACPolicy || hasOrgRBACPolicy || hasOrgRoleRBACPolicy;

      // hasPermission = hasPermission && hasResourcePermission;

      // const isAllowed = enforcer.enforceSync('admin', 'users', 'update_accommodations');

      // console.log('casbin isAllowed ==>', isAllowed); // This should be false with your provided policy
      // if (action === 'send_welcome_email_to_user') {
      //   console.log('accessControlProvider.can ===>', {
      //     ...params,
      //     currentUserRole,
      //     orgType,
      //     orgTypeWithRole,
      //     resource,
      //     action,
      //     hasUserRoleRBACPolicy,
      //     hasUserRolePermission,
      //     hasOrgRBACPolicy,
      //     hasOrgPermission,
      //     hasOrgRoleRBACPolicy,
      //     hasOrgRolePermission,
      //     hasResourcePermission,
      //     hasPermission,
      //   });
      // }

      return {
        can: hasPermission,
        reason,
      };
    } catch (error) {
      console.error('Access control error:', error);
      return { can: false };
    }
  },
};
